import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { ApprovalReportRow, WorkOrderReport } from '../api/types.js';
import { buildDetailedReport } from '../reports/detailedReport.js';
import type { DetailedReport, ReportField } from '../reports/detailedReport.js';
import { Alert, Button, Spinner } from '../components/ui.js';

/**
 * The work order report as a document rather than a grid: the reading, printing, and
 * Word-download form of the same rows the Reports page tabulates. It is reached from
 * both portals and carries only the fields the reader's scope already returns, so the
 * customer copy and the VGS copy are the same document with different rows in it.
 */
export function DetailedReportPage() {
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const facilityId = params.get('facilityId') ?? '';
  const taskOrderId = params.get('taskOrderId') ?? '';

  const filters = useMemo(
    () => ({
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(facilityId ? { facilityId } : {}),
      ...(taskOrderId ? { taskOrderId } : {}),
    }),
    [from, to, facilityId, taskOrderId],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'detailed', filters],
    queryFn: async () => {
      // Both reports are already scoped to the reader's facilities; the approvals
      // report supplies the authority each work order was approved under, which the
      // work order report does not carry.
      const [report, approvals] = await Promise.all([
        apiRequest<WorkOrderReport>('/api/reports/work-orders', { query: filters }),
        apiRequest<{ rows: ApprovalReportRow[] }>('/api/reports/approvals', { query: filters }),
      ]);
      return buildDetailedReport(report.rows, approvals.rows, { from, to });
    },
  });

  const download = async () => {
    if (!data) return;
    setBusy(true);
    setError(null);
    try {
      const { buildDetailedReportDocxBlob } = await import('../reports/detailedReportDocx.js');
      const url = URL.createObjectURL(await buildDetailedReportDocxBlob(data));
      const link = document.createElement('a');
      link.href = url;
      link.download = `work-order-report-${new Date().toISOString().slice(0, 10)}.docx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('The document could not be generated. Reload the page and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-end gap-2 print:hidden">
        <Link to={`/reports${params.toString() === '' ? '' : `?${params.toString()}`}`}>
          <Button variant="secondary">Back to reports</Button>
        </Link>
        <Button variant="secondary" onClick={() => window.print()} disabled={!data}>
          Print or save as PDF
        </Button>
        <Button onClick={() => void download()} disabled={busy || !data}>
          {busy ? 'Preparing…' : 'Download Word document'}
        </Button>
      </div>

      {error && (
        <Alert variant="error" title="Download failed" className="mb-4 print:hidden">
          {error}
        </Alert>
      )}

      {isError && (
        <Alert variant="error" title="The report could not be run">
          Reload the page, or narrow the date range and try again.
        </Alert>
      )}

      {isLoading && <Spinner label="Running report" />}

      {data && <ReportDocument report={data} />}
    </div>
  );
}

function ReportDocument({ report }: { report: DetailedReport }) {
  return (
    <article className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-slate-200 print:rounded-none print:p-0 print:shadow-none print:ring-0">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-brand-800">{report.title}</h1>
        <p className="mt-1 text-sm text-slate-800">{report.scopeLine}</p>
        <p className="text-sm text-slate-500">Reporting period: {report.periodLabel}</p>
        <p className="text-sm text-slate-500">Generated {report.generatedLabel}</p>
      </header>

      {report.isEmpty ? (
        <p className="text-sm italic text-slate-600">No work orders fall within this report.</p>
      ) : (
        <>
          <Section title="Summary">
            <FieldTable fields={report.summary} />
          </Section>

          {report.locations.map((location) => (
            <Section key={location.key} title={`Service location — ${location.heading}`}>
              <FieldTable fields={location.details} />
            </Section>
          ))}

          <Section title="Work orders in the report">
            <div className="overflow-x-auto">
              <table className="min-w-full border border-slate-300 text-sm">
                <caption className="sr-only">Work orders in the report</caption>
                <thead>
                  <tr className="bg-slate-100">
                    {report.overviewHeaders.map((header) => (
                      <th
                        key={header}
                        scope="col"
                        className="border border-slate-300 px-2 py-1.5 text-left text-xs font-semibold text-brand-800"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.overviewRows.map((row) => (
                    <tr key={row[0]}>
                      {row.map((value, index) => (
                        <td
                          key={report.overviewHeaders[index]}
                          className="border border-slate-300 px-2 py-1.5 align-top text-slate-700"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Work order detail">
            {report.locations.map((location) => (
              <div key={location.key}>
                {report.locations.length > 1 && (
                  <h3 className="mt-6 text-base font-semibold text-slate-800">
                    {location.heading}
                  </h3>
                )}
                {location.workOrders.map((workOrder) => (
                  <section key={workOrder.number} className="mt-5 break-inside-avoid">
                    <h3 className="mb-2 text-base font-semibold text-brand-800">
                      {workOrder.number} — {workOrder.title}
                    </h3>
                    <FieldTable fields={workOrder.fields} />
                  </section>
                ))}
              </div>
            ))}
          </Section>
        </>
      )}

      <p className="mt-8 text-xs italic text-slate-500">{report.footnote}</p>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 border-b-2 border-slate-200 pb-1 text-lg font-semibold text-brand-800">
        {title}
      </h2>
      {children}
    </section>
  );
}

function FieldTable({ fields }: { fields: readonly ReportField[] }) {
  return (
    <table className="w-full border border-slate-300 text-sm">
      <caption className="sr-only">Report fields</caption>
      <tbody>
        {fields.map((field) => (
          <tr key={field.label}>
            <th
              scope="row"
              className="w-1/3 border border-slate-300 bg-slate-50 px-2 py-1.5 text-left align-top text-xs font-semibold text-slate-600"
            >
              {field.label}
            </th>
            <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700">
              {field.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
